/**
 * API access — settings panel (2026-09-06, flag integration_api).
 *
 * Renders inside Settings → Integrations. Lets a company admin manage
 * the Integration API without curl:
 *
 *   - API keys: create (plaintext shown ONCE), list, revoke.
 *   - Webhook endpoints: add (secret shown ONCE), enable/disable,
 *     rotate secret, send a test event, view recent deliveries and
 *     retry dead ones, delete.
 *
 * Everything here talks to the admin-session management routes under
 * /api/v1 (routes/integrationApi.js in the backend). Data routes take
 * an API key, not a session — nothing in this panel reads deal data.
 *
 * Non-admins see the lists read-only. The parent gates the whole
 * section on authUser.company.featureFlags.integration_api, but the
 * panel also handles a 403 FEATURE_NOT_ENABLED gracefully in case the
 * flag is pulled while the page is open.
 *
 * Reference for the developer-facing side: docs.trailercast.io →
 * API reference tab.
 */

import { useCallback, useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';
const DOCS_URL = 'https://docs.trailercast.io/api-reference/introduction';

const EVENT_LABELS = {
  'engagement.recorded': 'Prospect engagement (views, opens, room activity)',
  'call.summary.created': 'AI call summary created or regenerated',
  'decision_room.updated': 'Decision Room status / value / close date changed',
  'decision_room.esign.updated': 'Contract signature status changed',
  'integration.ping': 'Test pings',
};

function formatWhen(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function ApiAccessSettings({ authToken, isAdmin, onToast }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notEnabled, setNotEnabled] = useState(false);
  const [keys, setKeys] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);

  // Create-key form + the one-time reveal.
  const [newKeyName, setNewKeyName] = useState('');
  const [creatingKey, setCreatingKey] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null); // { name, key }

  // Add-endpoint form + the one-time secret reveal.
  const [showAddHook, setShowAddHook] = useState(false);
  const [hookUrl, setHookUrl] = useState('');
  const [hookDescription, setHookDescription] = useState('');
  const [hookAllEvents, setHookAllEvents] = useState(true);
  const [hookEvents, setHookEvents] = useState([]);
  const [addingHook, setAddingHook] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState(null); // { endpointId, secret, reason }

  // Per-endpoint busy state + expanded delivery log.
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [deliveries, setDeliveries] = useState({}); // endpointId → rows

  const request = useCallback(async (path, { method = 'GET', body } = {}) => {
    const r = await fetch(`${API_BASE}/api/v1${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${authToken}`,
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (r.status === 204) return null;
    const data = await r.json().catch(() => ({}));
    if (!r.ok) {
      const err = new Error(data.error || `Request failed (${r.status})`);
      err.code = data.code;
      err.status = r.status;
      throw err;
    }
    return data;
  }, [authToken]);

  const toast = useCallback((message) => { if (typeof onToast === 'function') onToast(message); }, [onToast]);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [k, w] = await Promise.all([request('/api-keys'), request('/webhooks')]);
      setKeys(k.data || []);
      setEndpoints(w.data || []);
      setEventTypes(w.eventTypes || []);
      setNotEnabled(false);
    } catch (err) {
      if (err.code === 'FEATURE_NOT_ENABLED') setNotEnabled(true);
      else setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => { refresh(); }, [refresh]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast(`${label} copied.`);
    } catch (_) {
      toast(`Could not copy — select the ${label.toLowerCase()} manually.`);
    }
  };

  // ─── Keys ──────────────────────────────────────────────────────────

  const createKey = async (e) => {
    if (e) e.preventDefault();
    const name = newKeyName.trim();
    if (!name) { setError('Give the key a name — usually the system that will hold it.'); return; }
    setCreatingKey(true);
    setError(null);
    try {
      const r = await request('/api-keys', { method: 'POST', body: { name } });
      setRevealedKey({ name, key: r.key });
      setNewKeyName('');
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreatingKey(false);
    }
  };

  const revokeKey = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Any system using it stops working immediately. This cannot be undone.`)) return;
    setBusyId(`key-${k.id}`);
    setError(null);
    try {
      await request(`/api-keys/${k.id}`, { method: 'DELETE' });
      toast(`Key "${k.name}" revoked.`);
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  // ─── Endpoints ─────────────────────────────────────────────────────

  const addEndpoint = async (e) => {
    if (e) e.preventDefault();
    const url = hookUrl.trim();
    if (!url) { setError('Enter the HTTPS URL that should receive events.'); return; }
    const events = hookAllEvents ? ['*'] : hookEvents;
    if (!events.length) { setError('Pick at least one event type, or choose All events.'); return; }
    setAddingHook(true);
    setError(null);
    try {
      const r = await request('/webhooks', {
        method: 'POST',
        body: { url, events, description: hookDescription.trim() || undefined },
      });
      setRevealedSecret({ endpointId: r.data.id, secret: r.secret, reason: 'created' });
      setHookUrl(''); setHookDescription(''); setHookAllEvents(true); setHookEvents([]);
      setShowAddHook(false);
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingHook(false);
    }
  };

  const setEnabled = async (ep, enabled) => {
    setBusyId(`hook-${ep.id}`);
    setError(null);
    try {
      await request(`/webhooks/${ep.id}`, { method: 'PATCH', body: { enabled } });
      toast(enabled ? 'Endpoint enabled. Events will be queued for it again.' : 'Endpoint disabled.');
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const rotateSecret = async (ep) => {
    if (!window.confirm('Rotate the signing secret? Deliveries are signed with the new secret from their next attempt, so update your receiver first.')) return;
    setBusyId(`hook-${ep.id}`);
    setError(null);
    try {
      const r = await request(`/webhooks/${ep.id}/rotate-secret`, { method: 'POST' });
      setRevealedSecret({ endpointId: ep.id, secret: r.secret, reason: 'rotated' });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const sendTest = async (ep) => {
    setBusyId(`hook-${ep.id}`);
    setError(null);
    try {
      const r = await request(`/webhooks/${ep.id}/test`, { method: 'POST' });
      const code = r.delivery && r.delivery.lastStatusCode;
      if (r.outcome === 'delivered') toast(`Test event delivered — your endpoint answered ${code}.`);
      else toast(`Test event failed — your endpoint answered ${code || 'nothing'}${r.delivery && r.delivery.lastError ? ` (${r.delivery.lastError})` : ''}.`);
      await refresh();
      if (expandedId === ep.id) await loadDeliveries(ep.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const deleteEndpoint = async (ep) => {
    if (!window.confirm(`Delete this endpoint? Pending deliveries to it are dropped.\n\n${ep.url}`)) return;
    setBusyId(`hook-${ep.id}`);
    setError(null);
    try {
      await request(`/webhooks/${ep.id}`, { method: 'DELETE' });
      toast('Endpoint deleted.');
      if (expandedId === ep.id) setExpandedId(null);
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const loadDeliveries = async (endpointId) => {
    try {
      const r = await request(`/webhooks/${endpointId}/deliveries?limit=25`);
      setDeliveries((prev) => ({ ...prev, [endpointId]: r.data || [] }));
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleDeliveries = async (ep) => {
    if (expandedId === ep.id) { setExpandedId(null); return; }
    setExpandedId(ep.id);
    await loadDeliveries(ep.id);
  };

  const retryDelivery = async (ep, d) => {
    setBusyId(`delivery-${d.id}`);
    setError(null);
    try {
      await request(`/webhooks/${ep.id}/deliveries/${d.id}/retry`, { method: 'POST' });
      toast('Delivery re-queued. It will be attempted within a few seconds.');
      await loadDeliveries(ep.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const toggleEventType = (t) => {
    setHookEvents((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };

  // ─── Render ────────────────────────────────────────────────────────

  if (loading) {
    return <div style={mutedText}>Loading API access…</div>;
  }

  if (notEnabled) {
    return (
      <div style={cardStyle}>
        <strong style={{ fontSize: 15 }}>API access</strong>
        <p style={smallMuted}>The Integration API isn't enabled for this workspace yet.</p>
      </div>
    );
  }

  const liveKeys = keys.filter((k) => !k.revokedAt);
  const revokedKeys = keys.filter((k) => k.revokedAt);

  return (
    <div>
      {error && <div style={errorStyle}>{error}</div>}

      {!isAdmin && (
        <div style={warnStyle}>Only company admins can create keys or change webhook endpoints. You can see what's configured below.</div>
      )}

      {/* ── One-time reveals ── */}
      {revealedKey && (
        <RevealPanel
          title={`API key "${revealedKey.name}"`}
          value={revealedKey.key}
          note="This is the only time the key is shown. Store it in your secret manager now — only a hash is kept on our side. If you lose it, revoke it and create another."
          onCopy={() => copy(revealedKey.key, 'API key')}
          onDismiss={() => setRevealedKey(null)}
        />
      )}
      {revealedSecret && (
        <RevealPanel
          title={revealedSecret.reason === 'rotated' ? 'New webhook signing secret' : 'Webhook signing secret'}
          value={revealedSecret.secret}
          note="This is the only time the secret is shown. Your receiver needs it to verify the X-TrailerCast-Signature header on every delivery."
          onCopy={() => copy(revealedSecret.secret, 'Signing secret')}
          onDismiss={() => setRevealedSecret(null)}
        />
      )}

      {/* ── API host ── */}
      <div style={cardStyle}>
        <div style={rowBetween}>
          <strong style={{ fontSize: 15 }}>API host</strong>
          <a href={DOCS_URL} target="_blank" rel="noopener noreferrer" style={linkStyle}>Developer guide ↗</a>
        </div>
        <p style={smallMuted}>Point your integration at this base URL. Every request takes an API key as a bearer token.</p>
        <div style={urlRowStyle}>
          <code style={codeStyle}>{API_BASE}/api/v1</code>
          <button type="button" onClick={() => copy(`${API_BASE}/api/v1`, 'Base URL')} style={smallBtnStyle}>Copy</button>
        </div>
      </div>

      {/* ── API keys ── */}
      <div style={cardStyle}>
        <div style={rowBetween}>
          <strong style={{ fontSize: 15 }}>API keys</strong>
          <span style={pillNeutral}>{liveKeys.length} active</span>
        </div>
        <p style={smallMuted}>
          A key reads this workspace only, and only reads — it can't change anything in TrailerCast.
          Make one key per system so a leak or a decommission affects nothing else.
        </p>

        {isAdmin && (
          <form onSubmit={createKey} style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <input
              type="text"
              placeholder="Key name — e.g. HubSpot sync"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              disabled={creatingKey}
              style={inputStyle}
              aria-label="Key name"
            />
            <button type="submit" disabled={creatingKey || !newKeyName.trim()} style={primaryBtnStyle(creatingKey || !newKeyName.trim())}>
              {creatingKey ? 'Creating…' : 'Create key'}
            </button>
          </form>
        )}

        {liveKeys.length === 0 && <p style={smallMuted}>No active keys yet.</p>}
        {liveKeys.map((k) => (
          <div key={k.id} style={listRow}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={rowTitle}>{k.name} <code style={codeInlineStyle}>{k.prefix}…</code></div>
              <div style={rowMeta}>
                Created {formatWhen(k.createdAt)} · Last used {k.lastUsedAt ? formatWhen(k.lastUsedAt) : 'never'}
              </div>
            </div>
            {isAdmin && (
              <button type="button" onClick={() => revokeKey(k)} disabled={busyId === `key-${k.id}`} style={dangerBtnStyle(busyId === `key-${k.id}`)}>
                Revoke
              </button>
            )}
          </div>
        ))}
        {revokedKeys.length > 0 && (
          <details style={{ marginTop: 8 }}>
            <summary style={smallMutedInline}>{revokedKeys.length} revoked</summary>
            {revokedKeys.map((k) => (
              <div key={k.id} style={{ ...listRow, opacity: 0.6 }}>
                <div style={{ flex: 1 }}>
                  <div style={rowTitle}>{k.name} <code style={codeInlineStyle}>{k.prefix}…</code></div>
                  <div style={rowMeta}>Revoked {formatWhen(k.revokedAt)}</div>
                </div>
              </div>
            ))}
          </details>
        )}
      </div>

      {/* ── Webhook endpoints ── */}
      <div style={cardStyle}>
        <div style={rowBetween}>
          <strong style={{ fontSize: 15 }}>Webhook endpoints</strong>
          {isAdmin && !showAddHook && (
            <button type="button" onClick={() => setShowAddHook(true)} style={secondaryBtnStyle(false)}>Add endpoint</button>
          )}
        </div>
        <p style={smallMuted}>
          We POST a signed event to each enabled URL when something happens. Delivery is at-least-once with retries
          over ~45 hours; verify the signature and dedupe on the event id.
        </p>

        {showAddHook && (
          <form onSubmit={addEndpoint} style={subCard}>
            <div style={subheadStyle}>New endpoint</div>
            <input
              type="url"
              placeholder="https://your-system.example.com/hooks/trailercast"
              value={hookUrl}
              onChange={(e) => setHookUrl(e.target.value)}
              disabled={addingHook}
              style={{ ...inputStyle, marginBottom: 8 }}
              aria-label="Endpoint URL"
            />
            <input
              type="text"
              placeholder="Description (optional) — e.g. Production CRM"
              value={hookDescription}
              onChange={(e) => setHookDescription(e.target.value)}
              disabled={addingHook}
              style={{ ...inputStyle, marginBottom: 10 }}
              aria-label="Endpoint description"
            />
            <label style={checkRow}>
              <input type="checkbox" checked={hookAllEvents} onChange={(e) => setHookAllEvents(e.target.checked)} disabled={addingHook} />
              <span>All events (including types added in the future)</span>
            </label>
            {!hookAllEvents && (
              <div style={{ marginLeft: 22, marginBottom: 6 }}>
                {eventTypes.filter((t) => t !== 'integration.ping').map((t) => (
                  <label key={t} style={checkRow}>
                    <input type="checkbox" checked={hookEvents.includes(t)} onChange={() => toggleEventType(t)} disabled={addingHook} />
                    <span><code style={codeInlineStyle}>{t}</code> — {EVENT_LABELS[t] || t}</span>
                  </label>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button type="submit" disabled={addingHook || !hookUrl.trim()} style={primaryBtnStyle(addingHook || !hookUrl.trim())}>
                {addingHook ? 'Adding…' : 'Add'}
              </button>
              <button type="button" onClick={() => { setShowAddHook(false); setHookUrl(''); setHookDescription(''); }} disabled={addingHook} style={secondaryBtnStyle(addingHook)}>
                Cancel
              </button>
            </div>
          </form>
        )}

        {endpoints.length === 0 && !showAddHook && <p style={smallMuted}>No endpoints yet. The API still works without one — webhooks only make data arrive sooner.</p>}

        {endpoints.map((ep) => {
          const busy = busyId === `hook-${ep.id}`;
          const rows = deliveries[ep.id];
          return (
            <div key={ep.id} style={subCard}>
              <div style={rowBetween}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={rowTitle}>
                    <code style={{ ...codeStyle, flex: 'unset' }}>{ep.url}</code>
                  </div>
                  <div style={rowMeta}>
                    {ep.description ? `${ep.description} · ` : ''}
                    {Array.isArray(ep.events) && ep.events.includes('*') ? 'All events' : `${(ep.events || []).length} event type${(ep.events || []).length === 1 ? '' : 's'}`}
                    {' · '}Last success {formatWhen(ep.lastSuccessAt)}
                  </div>
                </div>
                <span style={ep.enabled ? pillOnStyle : pillOffStyle}>{ep.enabled ? 'Enabled' : 'Disabled'}</span>
              </div>

              {!ep.enabled && ep.disabledReason && (
                <div style={warnStyle}>Disabled automatically: {ep.disabledReason}. Fix the receiver, then enable it and reconcile with the API for anything missed.</div>
              )}
              {ep.enabled && ep.consecutiveFailures > 0 && (
                <div style={warnStyle}>
                  {ep.consecutiveFailures} consecutive failure{ep.consecutiveFailures === 1 ? '' : 's'}
                  {ep.lastError ? ` — ${ep.lastError}` : ''} (last at {formatWhen(ep.lastErrorAt)}). Disables itself at 20.
                </div>
              )}

              {isAdmin && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  <button type="button" onClick={() => sendTest(ep)} disabled={busy || !ep.enabled} style={secondaryBtnStyle(busy || !ep.enabled)}>Send test</button>
                  <button type="button" onClick={() => setEnabled(ep, !ep.enabled)} disabled={busy} style={secondaryBtnStyle(busy)}>{ep.enabled ? 'Disable' : 'Enable'}</button>
                  <button type="button" onClick={() => rotateSecret(ep)} disabled={busy} style={secondaryBtnStyle(busy)}>Rotate secret</button>
                  <button type="button" onClick={() => toggleDeliveries(ep)} disabled={busy} style={secondaryBtnStyle(busy)}>{expandedId === ep.id ? 'Hide deliveries' : 'View deliveries'}</button>
                  <button type="button" onClick={() => deleteEndpoint(ep)} disabled={busy} style={dangerBtnStyle(busy)}>Delete</button>
                </div>
              )}
              {!isAdmin && (
                <div style={{ marginTop: 8 }}>
                  <button type="button" onClick={() => toggleDeliveries(ep)} style={secondaryBtnStyle(false)}>{expandedId === ep.id ? 'Hide deliveries' : 'View deliveries'}</button>
                </div>
              )}

              {expandedId === ep.id && (
                <div style={{ marginTop: 10 }}>
                  <div style={subheadStyle}>Recent deliveries</div>
                  {!rows && <div style={smallMuted}>Loading…</div>}
                  {rows && rows.length === 0 && <div style={smallMuted}>Nothing delivered to this endpoint yet.</div>}
                  {rows && rows.map((d) => (
                    <div key={d.id} style={listRow}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={rowTitle}>
                          <code style={codeInlineStyle}>{d.eventType}</code>{' '}
                          <span style={deliveryPill(d.status)}>{d.status}</span>
                        </div>
                        <div style={rowMeta}>
                          {formatWhen(d.createdAt)} · {d.attempts} attempt{d.attempts === 1 ? '' : 's'}
                          {d.lastStatusCode ? ` · HTTP ${d.lastStatusCode}` : ''}
                          {d.lastError ? ` · ${d.lastError}` : ''}
                          {d.status === 'pending' && d.nextAttemptAt ? ` · next ${formatWhen(d.nextAttemptAt)}` : ''}
                        </div>
                      </div>
                      {isAdmin && d.status !== 'delivered' && (
                        <button type="button" onClick={() => retryDelivery(ep, d)} disabled={busyId === `delivery-${d.id}`} style={smallBtnStyle}>Retry</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RevealPanel({ title, value, note, onCopy, onDismiss }) {
  return (
    <div style={revealStyle}>
      <div style={rowBetween}>
        <strong style={{ fontSize: 14 }}>{title}</strong>
        <button type="button" onClick={onDismiss} style={smallBtnStyle}>Done, I've saved it</button>
      </div>
      <div style={{ ...urlRowStyle, marginTop: 8 }}>
        <code style={codeStyle}>{value}</code>
        <button type="button" onClick={onCopy} style={smallBtnStyle}>Copy</button>
      </div>
      <p style={{ ...smallMuted, marginTop: 8, color: '#92400e' }}>{note}</p>
    </div>
  );
}

// ─── Styles (same vocabulary as DocuSignSettings) ─────────────────

const cardStyle = {
  background: 'var(--room-card, #ffffff)',
  border: '1px solid var(--room-border, #e2e8f0)',
  borderRadius: 12,
  padding: 16,
  marginTop: 12,
};
const subCard = {
  background: 'var(--room-surface, #f8fafc)',
  border: '1px solid var(--room-border-soft, #f1f5f9)',
  borderRadius: 10,
  padding: 12,
  marginTop: 10,
};
const revealStyle = {
  background: '#fffbeb',
  border: '1px solid #fcd34d',
  borderRadius: 12,
  padding: 14,
  marginTop: 12,
};
const rowBetween = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 };
const listRow = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '8px 0', borderTop: '1px solid var(--room-border-soft, #f1f5f9)',
};
const rowTitle = { fontSize: 13, fontWeight: 600, color: 'var(--room-text-strong, #1e293b)', wordBreak: 'break-all' };
const rowMeta = { fontSize: 12, color: 'var(--room-text-muted, #64748b)', marginTop: 2 };
const subheadStyle = {
  fontSize: 12, fontWeight: 600, color: 'var(--room-text-body, #475569)',
  textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6,
};
const mutedText = { color: 'var(--room-text-muted, #64748b)', fontSize: 13 };
const smallMuted = { margin: '0 0 8px', color: 'var(--room-text-muted, #64748b)', fontSize: 12.5, lineHeight: 1.5 };
const smallMutedInline = { color: 'var(--room-text-muted, #64748b)', fontSize: 12.5, cursor: 'pointer' };
const urlRowStyle = {
  display: 'flex', alignItems: 'center', gap: 8,
  background: 'var(--room-surface, #f8fafc)', border: '1px solid var(--room-border, #e2e8f0)', borderRadius: 8,
  padding: '8px 10px',
};
const codeStyle = {
  flex: 1, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12,
  color: 'var(--room-text-strong, #1e293b)', wordBreak: 'break-all',
};
const codeInlineStyle = {
  fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12,
  background: 'var(--room-border-soft, #f1f5f9)', padding: '1px 6px', borderRadius: 4,
};
const inputStyle = {
  flex: 1, width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: 8,
  border: '1px solid var(--room-border, #cbd5e1)', background: 'var(--room-card, #ffffff)',
  color: 'var(--room-text-strong, #1e293b)', fontSize: 13, fontFamily: 'inherit',
};
const checkRow = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--room-text-body, #475569)', marginBottom: 6 };
const linkStyle = { fontSize: 12.5, color: '#7c3aed', textDecoration: 'none', fontWeight: 600 };
const errorStyle = {
  marginBottom: 10, padding: '8px 12px', borderRadius: 8,
  background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 13,
};
const warnStyle = {
  margin: '8px 0', padding: '8px 12px', borderRadius: 8,
  background: '#fef3c7', border: '1px solid #fcd34d', color: '#92400e', fontSize: 12.5, lineHeight: 1.5,
};
const pillOnStyle = { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: '#dcfce7', color: '#166534', whiteSpace: 'nowrap' };
const pillOffStyle = { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: '#fee2e2', color: '#991b1b', whiteSpace: 'nowrap' };
const pillNeutral = { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: 'var(--room-border-soft, #f1f5f9)', color: 'var(--room-text-muted, #64748b)' };
function deliveryPill(status) {
  if (status === 'delivered') return pillOnStyle;
  if (status === 'dead') return pillOffStyle;
  return { ...pillNeutral, background: '#fef3c7', color: '#92400e' };
}
function primaryBtnStyle(disabled) {
  return {
    padding: '8px 16px', borderRadius: 8, border: 'none',
    background: '#7c3aed', color: '#fff', fontSize: 13, fontWeight: 600,
    fontFamily: 'inherit', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1,
    whiteSpace: 'nowrap',
  };
}
function secondaryBtnStyle(disabled) {
  return {
    padding: '7px 12px', borderRadius: 8,
    border: '1px solid #c4b5fd', background: 'var(--room-accent-soft, #faf5ff)', color: '#7c3aed',
    fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit',
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1, whiteSpace: 'nowrap',
  };
}
function dangerBtnStyle(disabled) {
  return {
    padding: '7px 12px', borderRadius: 8,
    border: '1px solid #fecaca', background: 'var(--room-card, #fff)', color: '#b91c1c',
    fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit',
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1, whiteSpace: 'nowrap',
  };
}
const smallBtnStyle = {
  padding: '6px 10px', borderRadius: 6,
  border: '1px solid var(--room-border, #cbd5e1)', background: 'var(--room-card, #fff)', color: 'var(--room-text-strong, #1e293b)',
  fontSize: 12, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap',
};
